import type { FC } from 'react';
import { FunctionalInterfaceWrapper } from './FunctionalInterface.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface FunctionalInterfaceProps { }

const FunctionalInterface: FC<FunctionalInterfaceProps> = () => (
   <FunctionalInterfaceWrapper>
      <header className="entry-header ">
         <h1 className="entry-title" itemProp="headline">Functional Interface</h1>
      </header>
      <br/>
      <div className="entry-content clear" itemProp="text">
         <ul className="text-align-justify">
            <li>A functional interface is one that contains only one abstract method. They can have only one functionality to exhibit. </li>
            <li>A functional interface can contain any number of default methods.</li>
            <li>In other words, it is Single Abstract Method Interfaces or SAM Interfaces. </li>
            <li>It’s a new feature introduced in Java, which helps to achieve a functional programming approach.</li>
            <li>Runnable, Action Listener, Comparable are some of the examples of functional interfaces. </li>
            <li><strong>@FunctionalInterface</strong> annotation ensures that the functional interface can’t have more than one abstract method. Its use is optional, but it’s best practice to use it with functional interfaces to avoid the addition of extra methods accidentally. If the interface has annotation and we try to have more than one abstract method, it throws a compiler error.</li>
            <br/>
            <li>Some built-in functional interfaces :
               <ul>
                  <li><strong>Predicate :</strong>
                     <ul>
                        <li>The Predicate interface has an abstract method test which gives a Boolean value as a result of the specified argument.</li>
                        <li> Syntax :
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                              {
                                 'public interface Predicate {\n'+
                                 '  public boolean test(T t);\n'+
                                 '}'
                              }
                           </SyntaxHighlighter>
                        </li>
                     </ul>
                  </li>
                  <br/>
                  <li><strong>BinaryOperator</strong> :
                     <ul>
                        <li>The BinaryOperator interface contains an abstract method apply which takes two arguments and returns a result of the same type.</li>
                        <li> Syntax :
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                              {
                                 'public interface BinaryOperator {\n'+
                                 '  public T apply(T x, T y);\n'+
                                 '}'
                              }
                           </SyntaxHighlighter>
                        </li>
                     </ul>
                  </li>
                  <br/>
                  <li><strong>Function :</strong>
                     <ul>
                        <li>The Function interface contains an abstract method apply which takes an argument of type T and returns a result of type R. </li>
                        <li>Syntax :
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                              {
                                 'public interface Function {\n'+
                                 '  public R apply(T t);\n'+
                                 '}'
                              }
                           </SyntaxHighlighter>
                        </li>
                     </ul>
                  </li>
               </ul>
            </li>
         </ul>
         <br/>
         <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
            {
               '//Custom Interface\n'+
               '\n'+
               '@FunctionalInterface\n'+
               'interface sayable {\n'+
               '  void say(String msg);\n'+
               '}\n'+
               '\n'+
               'public class FIdemo implements sayable {\n'+
               '  public void say(String msg) {\n'+
               '    System.out.println(msg);\n'+
               '  }\n'+
               '  public static void main(String args[]) {\n'+
               '    FIdemo demo = new FIdemo();\n'+
               '    demo.say("Hello There!!");\n'+
               '  }\n'+
               '}'
            }
         </SyntaxHighlighter>
         <br/>
         <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
            {
               '//Built-in Interface\n'+
               '\n'+
               'List <String> names = Arrays.asList("A", "B", "C");\n'+
               'Predicate <String> p = (s) -> s.startsWith("G");\n'+
               '\n'+
               'for (String n: names) {\n'+
               '  if (p.test(n)) System.out.println(n);\n'+
               '}'
            }
         </SyntaxHighlighter>
      </div>
   </FunctionalInterfaceWrapper>
);

export default FunctionalInterface;
